import React, { useCallback, useEffect, useRef, useState } from "react";

import { crmPostventaRolloApi } from "../crmPostventaRolloApi";
import { crmPostventaApi } from "../../crmpostventa/crmPostventaApi";
import { agruparClientesPorGrupo } from "../../crmpostventa/clienteGrouping";
import Modal from "../components/Modal";
import { todayIso } from "../rolloFormat";
import ClienteStep from "./ClienteStep";
import GestionStep, { ClienteHeader, ComercioField, ContextoError, ContextoLoading, GestionAbierta } from "./GestionStep";
import {
  SEARCH_DEBOUNCE_MS,
  SEARCH_MIN_LENGTH,
  applyComercioPrefill,
  buildClienteResumen,
  buildComercioOptions,
  buildCrearPayload,
  buildInitialForm,
  buildToastText,
  createCorrelationId,
  describeCreateError,
  fieldId,
  firstErrorKey,
  normalizeClienteSearch,
  validateComercio,
  validateGestionForm,
} from "./nuevaGestionModel";

const TITLE_ID = "crm-rollo-nueva-title";
const STEP_LABEL = (step) => `Nueva gestión de rollo · Paso ${step} de 2`;

const IDLE_SEARCH = { status: "idle", results: [], text: "" };
const IDLE_CONTEXTO = { status: "idle", data: null, error: null, clienteId: null };

/**
 * "Nueva gestión" of Rollo (design RolloNewSelect, S3): step 1 picks a client with the CRM client search
 * (grouped like the rest of CRM Postventa), step 2 picks the comercio (S3a: already chosen when the client has one,
 * required when it has several), loads GET contexto for an alta of that comercio and creates the gestión with that
 * alta's clienteId. Everything shown comes from those endpoints and from the current user of the queue
 * (asesorNombre).
 *
 * onCreated({ gestionId, toast }) runs after a successful POST; onViewOpen({ gestionId, cuit }) when the
 * comercio already has an open gestión and the asesor asks to see it. Both are expected to close the modal.
 */
export default function NuevaGestionModal({ darkMode = false, asesorNombre, returnFocusRef, onClose, onCreated, onViewOpen }) {
  const [step, setStep] = useState(1);
  const [query, setQuery] = useState("");
  const [search, setSearch] = useState(IDLE_SEARCH);
  const [selectedKey, setSelectedKey] = useState(null);
  // Client of step 2: its group key, its comercios and the header shown until the contexto answers.
  const [cliente, setCliente] = useState(null);
  const [comercioKey, setComercioKey] = useState("");
  const [contexto, setContexto] = useState(IDLE_CONTEXTO);
  const [abierta, setAbierta] = useState(null);
  const [form, setForm] = useState(null);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [focusRequest, setFocusRequest] = useState(0);
  const searchRequestRef = useRef(0);
  const contextoRequestRef = useRef(0);
  const attemptRef = useRef(null);
  const mountedRef = useRef(true);
  const searchInputRef = useRef(null);
  // Comercio whose prefill the form holds, and the control to focus once step 2 can show it.
  const formComercioRef = useRef(null);
  const focusTargetRef = useRef(null);
  const today = todayIso();
  const comercios = cliente?.comercios || [];
  const comercio = comercios.find((item) => item.key === comercioKey) || null;

  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    []
  );

  // ---- Step 1: debounced search; only the latest request may update the results.
  const runSearch = useCallback(async (text) => {
    searchRequestRef.current += 1;
    const requestId = searchRequestRef.current;
    setSearch((current) => ({ ...current, status: "loading" }));
    try {
      const data = await crmPostventaApi.buscarClientes(normalizeClienteSearch(text));
      if (!mountedRef.current || requestId !== searchRequestRef.current) return;
      const results = agruparClientesPorGrupo(Array.isArray(data) ? data : []);
      setSearch({ status: "done", results, text });
      setSelectedKey((current) => (results.some((cliente) => cliente.groupKey === current) ? current : null));
    } catch (searchError) {
      if (!mountedRef.current || requestId !== searchRequestRef.current) return;
      setSearch({ status: "error", results: [], text });
    }
  }, []);

  useEffect(() => {
    const text = query.trim();
    searchRequestRef.current += 1;
    if (text.length < SEARCH_MIN_LENGTH) {
      setSearch({ ...IDLE_SEARCH, text });
      setSelectedKey(null);
      return undefined;
    }

    setSearch((current) => ({ ...current, status: "loading" }));
    const handle = setTimeout(() => runSearch(text), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [query, runSearch]);

  useEffect(() => {
    if (step === 1) searchInputRef.current?.focus();
  }, [step]);

  // ---- Step 2: contexto of the chosen comercio and, when it has an open gestión, that gestión's estado/date.
  const showAbierta = useCallback(async (gestionId) => {
    setAbierta({ id: gestionId, gestion: null });
    try {
      const detalle = await crmPostventaRolloApi.obtenerGestion(gestionId);
      if (!mountedRef.current) return;
      setAbierta((current) => (current?.id === gestionId ? { id: gestionId, gestion: detalle?.gestion || null } : current));
    } catch (detailError) {
      // The warning still shows without estado and date.
    }
  }, []);

  // The form keeps what the asesor typed while the comercio is the same one; another comercio brings its own
  // terminal and prefill (applyComercioPrefill).
  const loadContexto = useCallback(
    async (target) => {
      contextoRequestRef.current += 1;
      const requestId = contextoRequestRef.current;
      const { clienteId } = target;
      setContexto({ ...IDLE_CONTEXTO, status: "loading", clienteId });
      setAbierta(null);
      setErrors({});
      setSubmitError(null);
      attemptRef.current = null;
      try {
        const data = await crmPostventaRolloApi.obtenerContexto(clienteId);
        if (!mountedRef.current || requestId !== contextoRequestRef.current) return;
        const sameComercio = formComercioRef.current === target.key;
        formComercioRef.current = target.key;
        setContexto({ status: "done", data: data || {}, error: null, clienteId });
        setForm((current) => (sameComercio && current ? current : applyComercioPrefill(current, data)));
        if (data?.gestionAbiertaId) showAbierta(data.gestionAbiertaId);
      } catch (loadError) {
        if (!mountedRef.current || requestId !== contextoRequestRef.current) return;
        setContexto({ status: "error", data: null, error: loadError, clienteId });
      }
    },
    [showAbierta]
  );

  // A new client starts a fresh form; the same client keeps its chosen comercio. With several comercios nothing is
  // requested until one is chosen (S3a).
  const goToStep2 = (groupKey) => {
    const group = search.results.find((item) => item.groupKey === groupKey);
    if (!group) return;
    const options = buildComercioOptions(group);
    const kept = cliente?.groupKey === groupKey && options.some((item) => item.key === comercioKey) ? comercioKey : "";
    const nextKey = kept || (options.length === 1 ? options[0].key : "");
    const target = options.find((item) => item.key === nextKey);

    setCliente({ groupKey, comercios: options, resumen: buildClienteResumen(group) });
    setComercioKey(nextKey);
    setSelectedKey(groupKey);
    setStep(2);
    formComercioRef.current = null;
    focusTargetRef.current = options.length > 1 ? "comercio" : "terminal";
    if (target) {
      setForm(null);
      loadContexto(target);
      return;
    }

    contextoRequestRef.current += 1;
    setContexto(IDLE_CONTEXTO);
    setAbierta(null);
    setErrors({});
    setSubmitError(null);
    attemptRef.current = null;
    setForm(buildInitialForm(null));
  };

  const changeComercio = (key) => {
    const target = comercios.find((item) => item.key === key);
    if (!target || key === comercioKey) return;
    setComercioKey(key);
    setForm((current) => (current ? { ...current, terminal: "" } : current));
    loadContexto(target);
  };

  const retryContexto = () => {
    if (!comercio) return;
    focusTargetRef.current = "terminal";
    loadContexto(comercio);
  };

  const backToStep1 = () => {
    contextoRequestRef.current += 1;
    setStep(1);
  };

  // Focus on entering step 2: the comercio select when it has to be chosen, else the first control of the form
  // once it shows. Choosing a comercio keeps the focus on its select. After a failed submit, the first invalid field.
  useEffect(() => {
    if (step !== 2) return;
    if (focusTargetRef.current === "comercio") {
      focusTargetRef.current = null;
      document.getElementById(fieldId("comercio"))?.focus();
    } else if (focusTargetRef.current === "terminal" && contexto.status === "done" && !abierta) {
      focusTargetRef.current = null;
      document.getElementById(fieldId("terminal"))?.focus();
    }
  }, [step, contexto.status, abierta]);

  useEffect(() => {
    if (!focusRequest) return;
    const key = firstErrorKey(errors);
    if (key) document.getElementById(fieldId(key))?.focus();
    // Only a new submit moves the focus, not every error change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusRequest]);

  const changeField = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
    setErrors((current) => {
      if (!current[key]) return current;
      const next = { ...current };
      delete next[key];
      return next;
    });
  };

  // One correlationId per submission: a retry of the same data (after a network error, for example) reuses it,
  // so the server answers the gestión it may already have created instead of creating a second one.
  const submit = async () => {
    if (saving || !form) return;
    const nextErrors = { ...validateComercio(comercios, comercioKey), ...validateGestionForm(form, today) };
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      setFocusRequest((count) => count + 1);
      return;
    }
    if (contexto.status !== "done") return;

    // contexto.clienteId is the alta of the chosen comercio: the gestión belongs to that comercio (S3a).
    const payload = buildCrearPayload(contexto.clienteId, form);
    const key = JSON.stringify(payload);
    if (attemptRef.current?.key !== key) {
      attemptRef.current = { key, correlationId: createCorrelationId() };
    }

    setSaving(true);
    setSubmitError(null);
    try {
      const response = await crmPostventaRolloApi.crearGestion({ ...payload, correlationId: attemptRef.current.correlationId });
      if (!mountedRef.current) return;
      onCreated?.({ gestionId: response?.gestion?.id ?? null, toast: buildToastText(contexto.data?.cliente, payload) });
    } catch (createError) {
      if (!mountedRef.current) return;
      setSaving(false);
      const gestionAbiertaId = createError?.status === 409 ? createError?.payload?.gestionAbiertaId : null;
      if (gestionAbiertaId) {
        showAbierta(gestionAbiertaId);
        return;
      }
      setSubmitError(describeCreateError(createError));
    }
  };

  const viewOpen = () => onViewOpen?.({ gestionId: abierta?.id ?? null, cuit: contexto.data?.cliente?.cuit || "" });

  // The form shows once the comercio's contexto answered, or while a client with several comercios has none
  // chosen yet (a hint replaces the context block and submitting asks for the comercio).
  const formVisible = !abierta && Boolean(form) && (comercio ? contexto.status === "done" : comercios.length > 1);

  const renderStep2Body = () => {
    if (formVisible) {
      return (
        <GestionStep
          contexto={comercio ? contexto.data : null}
          numeroComercio={comercio?.numero}
          form={form}
          errors={errors}
          saving={saving}
          submitError={submitError}
          asesorNombre={asesorNombre}
          today={today}
          onChange={changeField}
          onSubmit={submit}
        />
      );
    }
    if (contexto.status === "error") return <ContextoError message={contexto.error?.message} onRetry={retryContexto} />;
    if (abierta) return <GestionAbierta abierta={abierta} otrosComercios={comercios.length > 1} onViewOpen={viewOpen} />;
    return <ContextoLoading />;
  };

  const renderStep2 = () => (
    <>
      <ClienteHeader
        cliente={(contexto.status === "done" && contexto.data?.cliente) || cliente?.resumen}
        disabled={saving}
        onChangeCliente={backToStep1}
      />
      <ComercioField comercios={comercios} value={comercioKey} errors={errors} disabled={saving} onChange={changeComercio} />
      {renderStep2Body()}
    </>
  );

  const cancelButton = (label = "Cancelar") => (
    <button type="button" className="crm-rollo__btn crm-rollo__btn--secondary" disabled={saving} onClick={onClose}>
      {label}
    </button>
  );

  const renderFooter = () => {
    if (step === 1) {
      return (
        <>
          <span className="crm-rollo__grow">{selectedKey ? "" : "Elegí un cliente para continuar"}</span>
          {cancelButton()}
          <button
            type="button"
            className="crm-rollo__btn crm-rollo__btn--primary"
            disabled={!selectedKey}
            onClick={() => goToStep2(selectedKey)}
          >
            Seleccionar
          </button>
        </>
      );
    }

    if (abierta) {
      return (
        <>
          <span className="crm-rollo__grow" />
          <button type="button" className="crm-rollo__btn crm-rollo__btn--ghost" onClick={onClose}>
            Cerrar
          </button>
        </>
      );
    }

    if (!formVisible) {
      return (
        <>
          <span className="crm-rollo__grow" />
          {cancelButton()}
        </>
      );
    }

    return (
      <>
        <span className="crm-rollo__grow">
          <span className="crm-rollo__req" aria-hidden="true">
            *
          </span>{" "}
          Obligatorio
        </span>
        {cancelButton()}
        <button
          type="button"
          className="crm-rollo__btn crm-rollo__btn--primary"
          disabled={saving}
          aria-busy={saving || undefined}
          onClick={submit}
        >
          {saving ? (
            <>
              <span className="crm-rollo__spinner" aria-hidden="true" />
              Guardando…
            </>
          ) : (
            "Crear gestión"
          )}
        </button>
      </>
    );
  };

  return (
    <Modal
      titleId={TITLE_ID}
      title={step === 1 ? "Seleccionar cliente" : "Datos de la gestión"}
      stepLabel={STEP_LABEL(step)}
      steps={2}
      step={step}
      size={step === 1 || abierta ? "sm" : "md"}
      darkMode={darkMode}
      closeDisabled={saving}
      returnFocusRef={returnFocusRef}
      onClose={onClose}
      footer={renderFooter()}
    >
      {step === 1 ? (
        <ClienteStep
          inputRef={searchInputRef}
          query={query}
          onQueryChange={setQuery}
          search={search}
          selectedKey={selectedKey}
          onSelect={setSelectedKey}
          onConfirm={goToStep2}
          onRetry={() => runSearch(query.trim())}
        />
      ) : (
        renderStep2()
      )}
    </Modal>
  );
}
